import type { GpsFix } from '@/lib/location'
/**
 * One delivery as a draft on the phone (F4.2 → F4.10): a pure state machine. Screens dispatch
 * actions; the draft is saved to IndexedDB after every change (offline/drafts, kind
 * 'gate.delivery') so a reload or a dead battery loses nothing. Nothing is sent until the
 * delivery is closed (commit_delivery) or cancelled (void_delivery) — R01.
 */
import type {
  CommitDeliveryPayload,
  DisputeOutcome,
  LotPayload,
  StorageAnswer,
  TagEntry,
  TagVoidReason,
  VoidDeliveryPayload,
  VoidDeliveryReason,
  RefusedScan,
  VoidedTag,
  DisputePayload,
} from '../commands'
import { currentWeight, lotFigures } from './figures'
import { tenthsToString } from './moisture'
import type { SeasonLight } from './seasonLight'

export const DRAFT_KIND = 'gate.delivery'

/**
 * Build scope (roster config.scope, from uc-core SCOPE). V1 = Seva Mandir V1 (agreed 6 Oct 2026):
 * no bag count (F4.3 folds into F4.6: each bag is Good or Red tie as it is weighed), no price screen
 * (F4.5 is automatic), one moisture reading, no sample weight or photo, no tag scans on Cancel.
 */
export type Scope = 'V1' | 'FULL'

export const STEP_IDS = [
  'details', // F4.2 crop, field, deliverer, storage questions
  'check', // F4.3 bags brought, failed bags, probe draws
  'moisture', // F4.4 (MAIN lot only)
  'price', // F4.5 accept / divert (F4-B1)
  'tag', // F4.6 tag and weigh
  'sample', // F4.7 (MAIN lot only)
  'slip', // F4.8 totals, agree, slip
  'close', // F4.10 checks, close / cancel
] as const
export type StepId = (typeof STEP_IDS)[number]

/**
 * V1 paper back-entry: find, crop and two questions → moisture (groundnut) → tag and weigh → seal
 * → slip → close. Live days run the same steps from a task list in any order (v1Tasks).
 */
export const STEP_IDS_V1 = [
  'details',
  'moisture',
  'tag',
  'sample',
  'slip',
  'close',
] as const satisfies readonly StepId[]

export interface DraftFarmer {
  id: string
  name: string
  roster_no: string
  father_name: string | null
  phone_last4: string | null
  photo_url: string | null
  bank_verified: boolean
  account_last4: string | null
  /** V1: an account checked (₹1) and waiting for the office's approval (last 4). */
  bank_pending_last4?: string | null
  /** V1: that account was typed in with no internet and waits on this phone for the check. */
  bank_queued?: boolean
  /** V1: "Not now" on the bank step: no account today, so his payment waits for one. */
  bank_later?: boolean
}

export interface DraftWeight {
  id: string
  gross_g: number
  taken_at: string
  confirmed_twice: boolean
  supersedes_id: string | null
  /** Live photo of the scale display (required by new APP drafts; optional for legacy/paper). */
  photo_media_id?: string | null
}

export interface DraftBag {
  id: string
  /** Red-tie bag (failed a red line, F4.3). */
  red: boolean
  qr_payload: string
  entry: TagEntry
  sack_type_id: string
  tare_g: number
  /** A sack not on the kit list, typed at the gate (TYPED_AT_GATE): its name. */
  sack_name?: string | null
  weights: DraftWeight[]
  conv_reason: string | null
  conv_photo_media_id: string | null
  tagged_at: string
  /** Went home in a dispute after tagging (F4-B2.c). */
  withdrawn: boolean
}

export interface FailedBag {
  id: string
  reason_code: string | null
  photo_media_id: string | null
}

export interface DraftReading {
  id: string
  tenths: number
  bag_no: number | null
  taken_at: string
  chosen_by_farmer: boolean
  /** Live meter photo (required by new APP drafts; optional for legacy/paper). */
  photo_media_id?: string | null
}

export interface DraftDispute {
  id: string
  about: 'WEIGHT' | 'GRADE' | 'MOISTURE'
  reason_code: string
  raised_at: string
  container_id: string | null
  bag_position: number | null
  disputed_measurement_id: string | null
  recheck_measurement_ids: string[]
  photo_ids: string[]
  test_weight: { id: string; test_weight_g: number; read_g: number; taken_at: string } | null
  chosen_by_farmer: boolean | null
  outcome: DisputeOutcome | null
  resolution_code: DisputePayload['resolution_code']
  closed_at: string | null
  /** The step to go back to once the dispute has an outcome. */
  return_step: StepId
}

export interface DraftSample {
  id: string
  seal_payload: string
  seal_entry: TagEntry
  /** V1: not weighed (null). */
  weight_g: number | null
  collected_at: string
  /** V1: no photo (null). */
  photo_media_id: string | null
  underweight_allowed_by: string | null
}

export interface DeliveryDraft {
  v: 1
  /** Absent on a draft started before scopes: the full spec. */
  scope?: Scope
  /**
   * New live deliveries require a photo for every scale and moisture reading. Absent on drafts
   * created by older app builds so an already-started offline delivery can still be completed.
   */
  reading_photos_required?: boolean
  /**
   * New live deliveries close only with the phone's position (or a supervisor's leave). Absent on
   * drafts created by older app builds and on paper back-entry.
   */
  location_required?: boolean
  /** Where the phone stood when the purchase was closed. */
  purchase_gps?: GpsFix | null
  /** The supervisor who let it close without a position (GPS impossible here). */
  location_skipped_by?: string | null
  /** V1: the picked crop needs a moisture reading (groundnut), from the roster's crop rules. */
  moisture_required?: boolean
  delivery_ulid: string
  session_id: string
  capture_mode: 'APP' | 'PAPER_BACKFILL'
  step: StepId
  /** The furthest step reached: going back to look at an earlier step keeps it (absent on old drafts). */
  reached?: StepId
  started_at: string
  farmer: DraftFarmer
  identification: {
    method: 'SEARCH' | 'ROSTER_NO'
    /** V1: null — no phone check, only the photo. */
    phone_match: 'MATCHED' | 'MISMATCH' | 'NO_PHONE' | null
    candidates_shown: number
    /** V1: he had no photo on record; the operator took one (media id). Becomes his photo. */
    new_photo_media_id?: string | null
  }
  identified_at: string
  crop_id: string | null
  cultivation_id: string | null
  crop_variety_id: string | null
  /** 'Another field (not registered)': the whole delivery is ordinary (CONV). */
  another_field: boolean
  delivered_by:
    | { kind: 'SELF' }
    | { kind: 'OTHER'; name: string; phone: string; photo_media_id: string | null }
    | null
  storage: {
    stored_over_7_days: StorageAnswer | null
    tablet_or_powder: StorageAnswer | null
    answered_at: string | null
  }
  /** Lot A: the good-bag lot (or the only lot). Minted when the draft starts. */
  main_lot: { id: string; code: string }
  /** Lot B: the red-tie sibling, minted when good and failed bags are both present. */
  conv_lot: { id: string; code: string } | null
  bags_brought: number | null
  bags_brought_confirmed: boolean
  failed_bags: FailedBag[]
  probe_draws_done: boolean
  moisture_bag_nos: number[] | null
  moisture: {
    equipment_id: string | null
    readings: DraftReading[]
    saved: boolean
    no_meter: boolean
    skip_allowed_by: string | null
  }
  outcome: { accepted_at: string } | null
  bags: DraftBag[]
  voided_tags: VoidedTag[]
  refused_scans: RefusedScan[]
  last_sack_type_id: string | null
  sample: DraftSample | null
  disputes: DraftDispute[]
  /** Dispute in progress (form / re-check / outcome screens). */
  active_dispute_id: string | null
  season_light: SeasonLight | null
  slip: {
    farmer_agreed_at: string | null
    account_confirmed: boolean
    disputed: boolean
    slip_no: string | null
    photo_media_id: string | null
  }
  /** F4-B3 back-entry: the paper slip being typed in. */
  paper: {
    /** V1: no receipt book (null); the slip number alone is sent. */
    book_id: string | null
    book_no: string | null
    slip_no: string
    /** Time written on the slip (ISO) — occurred_at of everything in this delivery. */
    slip_time: string
    page_photo_media_id: string | null
    receipt_slip_id: string
  } | null
  /** Cancel in progress: every tag and the seal must be scanned void first (F4.10). */
  cancel: { reason: VoidDeliveryReason; reason_code: string | null; started_at: string } | null
}

export interface NewDraftInput {
  delivery_ulid: string
  session_id: string
  capture_mode: 'APP' | 'PAPER_BACKFILL'
  now: string
  farmer: DraftFarmer
  identification: DeliveryDraft['identification']
  main_lot: { id: string; code: string }
  paper?: DeliveryDraft['paper']
  scope?: Scope
}

export function newDraft(i: NewDraftInput): DeliveryDraft {
  return {
    v: 1,
    scope: i.scope ?? 'FULL',
    reading_photos_required: i.capture_mode === 'APP',
    location_required: i.capture_mode === 'APP',
    moisture_required: false,
    delivery_ulid: i.delivery_ulid,
    session_id: i.session_id,
    capture_mode: i.capture_mode,
    step: 'details',
    started_at: i.now,
    farmer: i.farmer,
    identification: i.identification,
    identified_at: i.now,
    crop_id: null,
    cultivation_id: null,
    crop_variety_id: null,
    another_field: false,
    delivered_by: null,
    storage: { stored_over_7_days: null, tablet_or_powder: null, answered_at: null },
    main_lot: i.main_lot,
    conv_lot: null,
    bags_brought: null,
    bags_brought_confirmed: false,
    failed_bags: [],
    probe_draws_done: false,
    moisture_bag_nos: null,
    moisture: {
      equipment_id: null,
      readings: [],
      saved: false,
      no_meter: false,
      skip_allowed_by: null,
    },
    outcome: null,
    bags: [],
    voided_tags: [],
    refused_scans: [],
    last_sack_type_id: null,
    sample: null,
    disputes: [],
    active_dispute_id: null,
    season_light: null,
    slip: {
      farmer_agreed_at: null,
      account_confirmed: false,
      disputed: false,
      slip_no: i.paper?.slip_no ?? null,
      photo_media_id: null,
    },
    paper: i.paper ?? null,
    cancel: null,
  }
}

// ---------------------------------------------------------------------------------------------
// Derived state
// ---------------------------------------------------------------------------------------------

/** Seva Mandir V1 draft. */
export function isV1(d: Pick<DeliveryDraft, 'scope'>): boolean {
  return d.scope === 'V1'
}

/** V1: bags tagged Good and Red tie so far (the lots come from the bags, not from a count). */
export function v1Counts(d: DeliveryDraft): { good: number; red: number } {
  const live = d.bags.filter((b) => !b.withdrawn)
  return { good: live.filter((b) => !b.red).length, red: live.filter((b) => b.red).length }
}

// ---------------------------------------------------------------------------------------------
// V1 task list: after the crop, independent tasks in any order, then the slip
// ---------------------------------------------------------------------------------------------

export const V1_TASKS = ['forms', 'moisture', 'bags', 'seal', 'bank'] as const
export type V1TaskId = (typeof V1_TASKS)[number]

export interface V1Task {
  id: V1TaskId
  /** Must be done before the slip (moisture is optional except for groundnut). */
  required: boolean
  done: boolean
}

/** At least one bag, and every bag has its sack type and a weight (with its photo when asked). */
export function v1BagsDone(d: DeliveryDraft): boolean {
  return (
    d.bags.length > 0 &&
    d.bags.every(
      (b) =>
        b.sack_type_id &&
        bagWeight(b) !== null &&
        bagWeight(b)!.gross_g - b.tare_g > 0 &&
        (!d.reading_photos_required || b.weights.every((w) => !!w.photo_media_id)),
    )
  )
}

/** A moisture reading (with its photo when asked) or 'No meter'. */
export function v1MoistureDone(d: DeliveryDraft): boolean {
  return (
    d.moisture.saved &&
    (!d.reading_photos_required ||
      d.moisture.no_meter ||
      d.moisture.readings.every((r) => !!r.photo_media_id))
  )
}

/**
 * The V1 live delivery's tasks, in the list's order. The seal is not needed once the bags are
 * all red-tie (no good-bag lot); a seal already scanned is then voided at close.
 */
export function v1Tasks(d: DeliveryDraft): V1Task[] {
  const sealNeeded = !v1BagsDone(d) || hasMainLot(d)
  return [
    {
      id: 'forms',
      required: true,
      done: d.storage.stored_over_7_days !== null && d.storage.tablet_or_powder !== null,
    },
    { id: 'moisture', required: !!d.moisture_required, done: v1MoistureDone(d) },
    { id: 'bags', required: true, done: v1BagsDone(d) },
    { id: 'seal', required: sealNeeded, done: d.sample !== null },
    {
      id: 'bank',
      required: true,
      done: d.farmer.bank_verified || !!d.farmer.bank_pending_last4 || !!d.farmer.bank_later,
    },
  ]
}

/** Required tasks still open, in the list's order (Home: "Left: Seal, Bank"). */
export function v1TasksLeft(d: DeliveryDraft): V1TaskId[] {
  return v1Tasks(d)
    .filter((t) => t.required && !t.done)
    .map((t) => t.id)
}

/** The sack type whose weight is typed at the gate (uc-core TYPED_SACK_CODE). */
export const TYPED_SACK_CODE = 'TYPED_AT_GATE'

export type LotSlot = 'A' | 'B'

export interface LotPlan {
  slot: LotSlot
  id: string
  code: string
  stream: 'MAIN' | 'CONV'
  /** Bags this lot must tag (incl. ones later withdrawn in a dispute). */
  planned: number
  withdrawn: number
  /** Bags in the lot: planned − withdrawn. */
  bag_count: number
}

export function counts(d: DeliveryDraft) {
  const brought = d.bags_brought ?? 0
  const failed = d.failed_bags.length
  return { brought, failed, good: Math.max(0, brought - failed) }
}

/** One lot, or two when some (not all) bags failed (F4.3); 'Another field' is one CONV lot. */
export function lotPlan(d: DeliveryDraft): LotPlan[] {
  if (isV1(d)) return v1LotPlan(d)
  const { brought, failed, good } = counts(d)
  const withdrawnIn = (slot: LotSlot) =>
    d.bags.filter((b) => b.withdrawn && slotOf(d, b) === slot).length
  const lot = (
    slot: LotSlot,
    ref: { id: string; code: string },
    stream: 'MAIN' | 'CONV',
    planned: number,
  ): LotPlan => {
    const withdrawn = withdrawnIn(slot)
    return {
      slot,
      id: ref.id,
      code: ref.code,
      stream,
      planned,
      withdrawn,
      bag_count: planned - withdrawn,
    }
  }
  if (d.another_field || (good === 0 && failed > 0)) return [lot('A', d.main_lot, 'CONV', brought)]
  const out = [lot('A', d.main_lot, 'MAIN', good)]
  if (failed > 0 && d.conv_lot) out.push(lot('B', d.conv_lot, 'CONV', failed))
  return out
}

/** V1: good bags make lot A (MAIN); red-tie bags make lot B (CONV), or lot A when all are red. */
function v1LotPlan(d: DeliveryDraft): LotPlan[] {
  const { good, red } = v1Counts(d)
  const lot = (slot: LotSlot, ref: { id: string; code: string }, stream: 'MAIN' | 'CONV') => {
    const n = stream === 'MAIN' ? good : red
    return { slot, id: ref.id, code: ref.code, stream, planned: n, withdrawn: 0, bag_count: n }
  }
  if (good === 0 && red > 0) return [lot('A', d.main_lot, 'CONV')]
  const out = [lot('A', d.main_lot, 'MAIN')]
  if (red > 0 && d.conv_lot) out.push(lot('B', d.conv_lot, 'CONV'))
  return out
}

export function slotOf(d: DeliveryDraft, bag: Pick<DraftBag, 'red'>): LotSlot {
  if (isV1(d)) return bag.red && v1Counts(d).good > 0 && d.conv_lot ? 'B' : 'A'
  const { failed, good } = counts(d)
  const twoLots = !d.another_field && good > 0 && failed > 0 && d.conv_lot !== null
  return bag.red && twoLots ? 'B' : 'A'
}

export function lotIdOf(d: DeliveryDraft, bag: Pick<DraftBag, 'red'>): string {
  return slotOf(d, bag) === 'B' && d.conv_lot ? d.conv_lot.id : d.main_lot.id
}

/** Has a good-bag (MAIN) lot: moisture and sample apply only then. */
export function hasMainLot(d: DeliveryDraft): boolean {
  if (isV1(d)) return v1Counts(d).good > 0
  return lotPlan(d).some((l) => l.stream === 'MAIN' && l.planned > 0)
}

export function visibleSteps(d: DeliveryDraft): StepId[] {
  if (isV1(d)) {
    // Moisture for groundnut only (before tagging); the seal once there is a good bag.
    return STEP_IDS_V1.filter((s) =>
      s === 'moisture'
        ? !!d.moisture_required
        : s === 'sample'
          ? d.bags.length === 0 || hasMainLot(d)
          : true,
    )
  }
  const main = d.bags_brought === null ? true : hasMainLot(d)
  return STEP_IDS.filter((s) => (s === 'moisture' || s === 'sample' ? main : true))
}

/** How far this delivery got: Home's progress and where it resumes (not the step last looked at). */
export function furthestStep(d: DeliveryDraft): StepId {
  return d.reached && STEP_IDS.indexOf(d.reached) > STEP_IDS.indexOf(d.step) ? d.reached : d.step
}

export function nextStep(d: DeliveryDraft, from: StepId = d.step): StepId {
  const steps = visibleSteps(d)
  const i = steps.indexOf(from)
  return steps[Math.min(steps.length - 1, i + 1)] ?? from
}

export function prevStep(d: DeliveryDraft, from: StepId = d.step): StepId {
  const steps = visibleSteps(d)
  const i = steps.indexOf(from)
  return steps[Math.max(0, i - 1)] ?? from
}

/** Tagging phase (F4.6): good bags first, then red-tie bags. */
export function tagPhase(d: DeliveryDraft): {
  phase: 'GOOD' | 'RED' | 'DONE'
  index: number
  of: number
} {
  const { good, failed } = counts(d)
  const taggedGood = d.bags.filter((b) => !b.red).length
  const taggedRed = d.bags.filter((b) => b.red).length
  if (taggedGood < good) return { phase: 'GOOD', index: taggedGood + 1, of: good }
  if (taggedRed < failed) return { phase: 'RED', index: taggedRed + 1, of: failed }
  return { phase: 'DONE', index: 0, of: 0 }
}

/** The red-line reason and photo the next red-tie bag carries (k-th red bag ↔ k-th failed bag). */
export function nextRedInfo(d: DeliveryDraft): FailedBag | null {
  const taggedRed = d.bags.filter((b) => b.red).length
  return d.failed_bags[taggedRed] ?? null
}

/** Payloads already used in this draft (bags + seal), for the duplicate-scan guard. */
export function draftPayloads(d: DeliveryDraft): Set<string> {
  const s = new Set(d.bags.map((b) => b.qr_payload))
  if (d.sample) s.add(d.sample.seal_payload)
  return s
}

export function bagsOfSlot(d: DeliveryDraft, slot: LotSlot): DraftBag[] {
  return d.bags.filter((b) => slotOf(d, b) === slot)
}

/** The weight that counts now for a bag. */
export function bagWeight(b: DraftBag): DraftWeight | null {
  return currentWeight(b.weights)
}

export function openDispute(d: DeliveryDraft): DraftDispute | null {
  return d.disputes.find((x) => x.id === d.active_dispute_id) ?? null
}

// ---------------------------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------------------------

export type DeliveryAction =
  | { type: 'goto'; step: StepId }
  | {
      type: 'details'
      patch: Partial<
        Pick<
          DeliveryDraft,
          | 'crop_id'
          | 'cultivation_id'
          | 'crop_variety_id'
          | 'another_field'
          | 'delivered_by'
          | 'season_light'
          | 'moisture_required'
        >
      >
    }
  | {
      type: 'storage'
      field: 'stored_over_7_days' | 'tablet_or_powder'
      value: StorageAnswer
      at: string
    }
  | { type: 'bags_brought'; n: number | null; confirmed?: boolean }
  | { type: 'add_failed_bag'; id: string }
  | { type: 'update_failed_bag'; id: string; patch: Partial<Omit<FailedBag, 'id'>> }
  | { type: 'remove_failed_bag'; id: string }
  | { type: 'probe_draws'; done: boolean }
  | { type: 'moisture_bags'; nos: number[] | null }
  | {
      type: 'check_done'
      moisture_bag_nos: number[]
      conv_lot: { id: string; code: string } | null
    }
  | { type: 'meter'; equipment_id: string | null }
  | { type: 'save_moisture'; readings: DraftReading[] }
  | { type: 'no_meter' }
  /** V1: his bank account was checked at the gate; it waits for the office's approval. */
  | { type: 'bank_added'; last4: string; queued?: boolean }
  /** V1: "Not now": he has no account details today; the purchase goes on, the payment waits. */
  | { type: 'bank_later' }
  | { type: 'moisture_skip'; by: string }
  | { type: 'purchase_gps'; gps: GpsFix }
  | { type: 'location_skip'; by: string }
  | { type: 'accept'; at: string }
  | {
      type: 'tag_bag'
      bag: Omit<DraftBag, 'red' | 'conv_reason' | 'conv_photo_media_id' | 'withdrawn'>
      /** V1: Good or Red tie, picked on the weight screen. */
      red?: boolean
      /** V1: the red-tie lot, minted when the delivery first has both good and red-tie bags. */
      conv_lot?: { id: string; code: string }
    }
  | { type: 'fix_last_bag'; weight: DraftWeight; sack?: { sack_type_id: string; tare_g: number } }
  | { type: 'void_tag'; payload: string; kind: 'BAG' | 'SEAL'; reason: TagVoidReason; at: string }
  | { type: 'refuse_scan'; scan: RefusedScan }
  | { type: 'set_sample'; sample: DraftSample | null }
  | { type: 'dispute_raise'; dispute: DraftDispute }
  | { type: 'dispute_reweigh'; id: string; weight: DraftWeight }
  | { type: 'dispute_redraw'; id: string; readings: DraftReading[] }
  | {
      type: 'dispute_update'
      id: string
      patch: Partial<Pick<DraftDispute, 'photo_ids' | 'test_weight' | 'chosen_by_farmer'>>
    }
  | {
      type: 'dispute_outcome'
      id: string
      outcome: DisputeOutcome
      resolution_code: DisputePayload['resolution_code']
      at: string
    }
  | { type: 'dispute_abandon'; id: string }
  | { type: 'farmer_agrees'; at: string; account_confirmed: boolean }
  | { type: 'slip'; patch: Partial<Pick<DeliveryDraft['slip'], 'slip_no' | 'photo_media_id'>> }
  | { type: 'paper'; patch: Partial<NonNullable<DeliveryDraft['paper']>> }
  | { type: 'start_cancel'; reason: VoidDeliveryReason; reason_code: string | null; at: string }
  | { type: 'abort_cancel' }

/** Figures changed after he agreed: he must see and agree again. */
function unagree(d: DeliveryDraft): DeliveryDraft {
  return d.slip.farmer_agreed_at ? { ...d, slip: { ...d.slip, farmer_agreed_at: null } } : d
}

export function deliveryReducer(d: DeliveryDraft, a: DeliveryAction): DeliveryDraft {
  switch (a.type) {
    case 'goto': {
      const far = furthestStep(d)
      return {
        ...d,
        step: a.step,
        reached: STEP_IDS.indexOf(a.step) > STEP_IDS.indexOf(far) ? a.step : far,
      }
    }
    case 'details':
      return { ...d, ...a.patch }
    case 'storage': {
      const storage = { ...d.storage, [a.field]: a.value }
      const done = storage.stored_over_7_days !== null && storage.tablet_or_powder !== null
      return {
        ...d,
        storage: { ...storage, answered_at: done ? (d.storage.answered_at ?? a.at) : null },
      }
    }
    case 'bags_brought':
      if (a.n !== null && a.n < d.bags.length) return d // cannot go below bags already tagged
      return unagree({
        ...d,
        bags_brought: a.n,
        bags_brought_confirmed: a.confirmed ?? false,
        moisture_bag_nos: null,
      })
    case 'add_failed_bag':
      return {
        ...d,
        failed_bags: [...d.failed_bags, { id: a.id, reason_code: null, photo_media_id: null }],
        moisture_bag_nos: null,
      }
    case 'update_failed_bag':
      return {
        ...d,
        failed_bags: d.failed_bags.map((f) => (f.id === a.id ? { ...f, ...a.patch } : f)),
      }
    case 'remove_failed_bag':
      return {
        ...d,
        failed_bags: d.failed_bags.filter((f) => f.id !== a.id),
        moisture_bag_nos: null,
      }
    case 'probe_draws':
      return { ...d, probe_draws_done: a.done }
    case 'moisture_bags':
      return { ...d, moisture_bag_nos: a.nos }
    case 'check_done': {
      const next = {
        ...d,
        moisture_bag_nos: d.moisture_bag_nos ?? a.moisture_bag_nos,
        conv_lot: d.conv_lot ?? a.conv_lot,
      }
      return { ...next, step: nextStep(next, 'check') }
    }
    case 'meter':
      return { ...d, moisture: { ...d.moisture, equipment_id: a.equipment_id } }
    case 'save_moisture':
      // V1: the reading can be changed until he agrees the slip; the full flow keeps it.
      if (d.moisture.saved && !isV1(d)) return d // "after Save it stays"
      return {
        ...d,
        moisture: {
          ...d.moisture,
          readings: a.readings,
          saved: true,
          no_meter: false,
          skip_allowed_by: null,
        },
      }
    case 'bank_added':
      return {
        ...d,
        farmer: {
          ...d.farmer,
          bank_pending_last4: a.last4,
          bank_queued: Boolean(a.queued),
          bank_later: false,
        },
      }
    case 'bank_later':
      return { ...d, farmer: { ...d.farmer, bank_later: true } }
    case 'no_meter':
      return {
        ...d,
        moisture: {
          ...d.moisture,
          readings: [],
          saved: true,
          no_meter: true,
          skip_allowed_by: null,
        },
      }
    case 'moisture_skip':
      return {
        ...d,
        moisture: {
          ...d.moisture,
          readings: [],
          saved: true,
          no_meter: true,
          skip_allowed_by: a.by,
        },
      }
    case 'purchase_gps':
      return { ...d, purchase_gps: a.gps, location_skipped_by: null }
    case 'location_skip':
      return { ...d, location_skipped_by: a.by }
    case 'accept':
      return { ...d, outcome: { accepted_at: a.at }, step: nextStep(d, 'price') }
    case 'tag_bag': {
      if (isV1(d)) {
        const bag: DraftBag = {
          ...a.bag,
          red: !!a.red,
          conv_reason: null,
          conv_photo_media_id: null,
          withdrawn: false,
        }
        return unagree({
          ...d,
          bags: [...d.bags, bag],
          last_sack_type_id: a.bag.sack_type_id,
          conv_lot: d.conv_lot ?? a.conv_lot ?? null,
        })
      }
      const phase = tagPhase(d)
      if (phase.phase === 'DONE') return d // more bags than counted: fix the count in F4.3
      const red = phase.phase === 'RED'
      const info = red ? nextRedInfo(d) : null
      const bag: DraftBag = {
        ...a.bag,
        red,
        conv_reason: info?.reason_code ?? null,
        conv_photo_media_id: info?.photo_media_id ?? null,
        withdrawn: false,
      }
      return unagree({ ...d, bags: [...d.bags, bag], last_sack_type_id: a.bag.sack_type_id })
    }
    case 'fix_last_bag': {
      const last = d.bags.at(-1)
      if (!last) return d
      const current = bagWeight(last)
      const fixed: DraftBag = {
        ...last,
        ...(a.sack ? { sack_type_id: a.sack.sack_type_id, tare_g: a.sack.tare_g } : {}),
        weights: [...last.weights, { ...a.weight, supersedes_id: current?.id ?? null }],
      }
      return unagree({ ...d, bags: [...d.bags.slice(0, -1), fixed] })
    }
    case 'void_tag': {
      const voided: VoidedTag = {
        qr_payload: a.payload,
        kind: a.kind,
        void_reason: a.reason,
        container_id: null,
        occurred_at: a.at,
      }
      if (d.voided_tags.some((v) => v.qr_payload === a.payload)) return d
      if (d.cancel) {
        // Cancelling: the bags stay on record (void_delivery.bags), each tag scanned void.
        const bag = d.bags.find((b) => b.qr_payload === a.payload)
        return {
          ...d,
          voided_tags: [...d.voided_tags, { ...voided, container_id: bag?.id ?? null }],
        }
      }
      // In the draft: voiding a tag on a bag voids that bag; it needs a new tag and weight.
      const bags = d.bags.filter((b) => b.qr_payload !== a.payload)
      const sample = d.sample?.seal_payload === a.payload ? null : d.sample
      return unagree({ ...d, bags, sample, voided_tags: [...d.voided_tags, voided] })
    }
    case 'refuse_scan':
      return { ...d, refused_scans: [...d.refused_scans, a.scan] }
    case 'set_sample':
      return { ...d, sample: a.sample }
    case 'dispute_raise':
      return { ...d, disputes: [...d.disputes, a.dispute], active_dispute_id: a.dispute.id }
    case 'dispute_reweigh': {
      const disp = d.disputes.find((x) => x.id === a.id)
      if (!disp?.container_id) return d
      const bags = d.bags.map((b) => {
        if (b.id !== disp.container_id) return b
        return {
          ...b,
          weights: [
            ...b.weights,
            {
              ...a.weight,
              supersedes_id: disp.disputed_measurement_id ?? bagWeight(b)?.id ?? null,
            },
          ],
        }
      })
      return unagree({
        ...d,
        bags,
        disputes: d.disputes.map((x) =>
          x.id === a.id
            ? { ...x, recheck_measurement_ids: [...x.recheck_measurement_ids, a.weight.id] }
            : x,
        ),
      })
    }
    case 'dispute_redraw':
      return {
        ...d,
        moisture: { ...d.moisture, readings: [...d.moisture.readings, ...a.readings] },
        disputes: d.disputes.map((x) =>
          x.id === a.id
            ? {
                ...x,
                chosen_by_farmer: true,
                recheck_measurement_ids: [
                  ...x.recheck_measurement_ids,
                  ...a.readings.map((r) => r.id),
                ],
              }
            : x,
        ),
      }
    case 'dispute_update':
      return { ...d, disputes: d.disputes.map((x) => (x.id === a.id ? { ...x, ...a.patch } : x)) }
    case 'dispute_abandon':
      // Only before anything was re-checked: a recorded dispute with values is never dropped.
      return {
        ...d,
        disputes: d.disputes.filter(
          (x) => x.id !== a.id || x.recheck_measurement_ids.length > 0 || x.photo_ids.length > 0,
        ),
        active_dispute_id: null,
      }
    case 'dispute_outcome': {
      const disp = d.disputes.find((x) => x.id === a.id)
      if (!disp) return d
      let next: DeliveryDraft = {
        ...d,
        active_dispute_id: null,
        step: disp.return_step,
        disputes: d.disputes.map((x) =>
          x.id === a.id
            ? {
                ...x,
                outcome: a.outcome,
                resolution_code: a.resolution_code,
                closed_at: a.outcome === 'COMMITTED_DISPUTED' ? null : a.at,
              }
            : x,
        ),
      }
      if (a.outcome === 'BAG_WITHDRAWN') {
        if (disp.container_id) {
          const bag = d.bags.find((b) => b.id === disp.container_id)
          if (bag) {
            next = {
              ...next,
              bags: next.bags.map((b) => (b.id === bag.id ? { ...b, withdrawn: true } : b)),
              voided_tags: [
                ...next.voided_tags,
                {
                  qr_payload: bag.qr_payload,
                  kind: 'BAG',
                  void_reason: 'WITHDRAWN_IN_DISPUTE',
                  container_id: bag.id,
                  occurred_at: a.at,
                },
              ],
            }
          }
        } else if (disp.bag_position !== null) {
          // Not tagged yet: the phone lowers the bag count (and drops that failed-bag entry).
          const failed = next.failed_bags.filter((_, i) => i !== disp.bag_position! - 1)
          next = {
            ...next,
            failed_bags: failed,
            bags_brought: Math.max(0, (next.bags_brought ?? 0) - 1),
          }
        }
        next = unagree(next)
      }
      if (a.outcome === 'COMMITTED_DISPUTED')
        next = { ...next, slip: { ...next.slip, disputed: true } }
      return next
    }
    case 'farmer_agrees':
      return {
        ...d,
        slip: { ...d.slip, farmer_agreed_at: a.at, account_confirmed: a.account_confirmed },
      }
    case 'slip':
      return { ...d, slip: { ...d.slip, ...a.patch } }
    case 'paper':
      return d.paper ? { ...d, paper: { ...d.paper, ...a.patch } } : d
    case 'start_cancel':
      return { ...d, cancel: { reason: a.reason, reason_code: a.reason_code, started_at: a.at } }
    case 'abort_cancel':
      return { ...d, cancel: null }
  }
}

// ---------------------------------------------------------------------------------------------
// Cancel (F4.10 / F4-B1.a)
// ---------------------------------------------------------------------------------------------

/** Tags and seal still to scan void before a WALK_AWAY / DIVERTED_TO_DRY cancel can be sent. */
export function cancelPending(d: DeliveryDraft): { tags: string[]; seal: string | null } {
  // V1: Cancel voids the draft's codes itself; the tags are just cut off (no scans).
  if (isV1(d) || d.cancel?.reason === 'MOVED_TO_PAPER') return { tags: [], seal: null }
  const voided = new Set(d.voided_tags.map((v) => v.qr_payload))
  return {
    tags: d.bags.filter((b) => !voided.has(b.qr_payload)).map((b) => b.qr_payload),
    seal: d.sample && !voided.has(d.sample.seal_payload) ? d.sample.seal_payload : null,
  }
}

/** Divert is offered only while nothing is tagged or sealed (F4-B1.a). */
export function canDivert(d: DeliveryDraft): boolean {
  return d.bags.length === 0 && d.sample === null
}

// ---------------------------------------------------------------------------------------------
// Payloads
// ---------------------------------------------------------------------------------------------

export interface CardRef {
  id: string
  price_paise_per_kg: number
}

function moisturePayload(d: DeliveryDraft) {
  return {
    equipment_id: d.moisture.equipment_id,
    readings: d.moisture.readings.map((r) => ({
      id: r.id,
      value: tenthsToString(r.tenths),
      bag_no: r.bag_no,
      taken_at: r.taken_at,
      chosen_by_farmer: r.chosen_by_farmer,
      ...(r.photo_media_id ? { photo_media_id: r.photo_media_id } : {}),
    })),
    skipped: d.moisture.readings.length === 0,
    skip_allowed_by: d.moisture.skip_allowed_by,
  }
}

function bagsPayload(d: DeliveryDraft) {
  return d.bags.map((b) => ({
    id: b.id,
    unit_id: lotIdOf(d, b),
    qr_payload: b.qr_payload,
    entry: b.entry,
    sack_type_id: b.sack_type_id,
    tare_g: b.tare_g,
    ...(b.sack_name ? { sack_name: b.sack_name } : {}),
    weights: b.weights.map((w) => ({ ...w })),
    conv_reason: b.red ? b.conv_reason : null,
    conv_photo_media_id: b.red ? b.conv_photo_media_id : null,
    tagged_at: b.tagged_at,
    withdrawn: b.withdrawn,
  }))
}

function disputesPayload(d: DeliveryDraft): DisputePayload[] {
  return d.disputes
    .filter((x) => x.outcome !== null)
    .map((x) => ({
      id: x.id,
      about: x.about,
      reason_code: x.reason_code,
      container_id: x.container_id,
      bag_position: x.bag_position,
      disputed_measurement_id: x.disputed_measurement_id,
      raised_at: x.raised_at,
      recheck_measurement_ids: x.recheck_measurement_ids,
      photo_ids: x.photo_ids,
      test_weight: x.test_weight,
      chosen_by_farmer: x.chosen_by_farmer,
      outcome: x.outcome!,
      resolution_code: x.resolution_code,
      closed_at: x.closed_at,
    }))
}

/** Figures for each lot from its bags and its card: what the slip shows and phone_figures sends. */
export function lotsWithFigures(
  d: DeliveryDraft,
  cards: Partial<Record<'MAIN' | 'CONV', CardRef>>,
) {
  return lotPlan(d).map((l) => {
    const card = cards[l.stream]
    const bags = bagsOfSlot(d, l.slot)
    return {
      plan: l,
      card: card ?? null,
      bags,
      figures: lotFigures(bags, card?.price_paise_per_kg ?? 0),
    }
  })
}

/** The purchase position for the server; nothing for older drafts and paper back-entry. */
function purchaseGpsPayload(d: DeliveryDraft) {
  const g = d.purchase_gps
  return {
    ...(g
      ? {
          gps: {
            lat: g.lat,
            lng: g.lng,
            accuracy_m: Math.round(g.accuracy_m * 10) / 10,
            captured_at: g.captured_at,
          },
        }
      : {}),
    ...(d.location_skipped_by && !g ? { gps_skip_allowed_by: d.location_skipped_by } : {}),
  }
}

export function buildCommitPayload(
  d: DeliveryDraft,
  ctx: {
    cards: Partial<Record<'MAIN' | 'CONV', CardRef>>
    closed_at: string
    season_light: SeasonLight
  },
): CommitDeliveryPayload {
  if (!d.crop_id) throw new Error('crop missing')
  if (isV1(d)) return buildCommitPayloadV1(d, ctx)
  if (!d.outcome) throw new Error('not accepted')
  if (!d.slip.slip_no || !d.slip.photo_media_id) throw new Error('slip missing')
  const answers = d.storage
  if (!answers.stored_over_7_days || !answers.tablet_or_powder || !answers.answered_at) {
    throw new Error('storage answers missing')
  }
  const lots: LotPayload[] = lotsWithFigures(d, ctx.cards).map(({ plan, card, figures }) => {
    if (!card) throw new Error(`no rate card for ${plan.stream}`)
    return {
      id: plan.id,
      code: plan.code,
      stream: plan.stream,
      bag_count: plan.bag_count,
      crop_id: d.crop_id!,
      crop_variety_id: d.crop_variety_id,
      cultivation_id: d.another_field ? null : d.cultivation_id,
      no_cultivation_reason: d.another_field ? 'FIELD_NOT_ENROLLED' : null,
      rate_card_id: card.id,
      phone_figures: figures,
    }
  })
  const db = d.delivered_by
  return {
    ...(d.reading_photos_required ? { reading_photos_required: true as const } : {}),
    ...purchaseGpsPayload(d),
    delivery_ulid: d.delivery_ulid,
    session_id: d.session_id,
    farmer_id: d.farmer.id,
    crop_id: d.crop_id,
    identified_at: d.identified_at,
    identification: {
      method: d.identification.method,
      ...(d.identification.phone_match ? { phone_match: d.identification.phone_match } : {}),
      ...(d.identification.new_photo_media_id
        ? { new_photo_media_id: d.identification.new_photo_media_id }
        : {}),
      photo_confirmed: true,
      candidates_shown: d.identification.candidates_shown,
      season_light: ctx.season_light,
    },
    storage_answers: {
      stored_over_7_days: answers.stored_over_7_days,
      tablet_or_powder: answers.tablet_or_powder,
      answered_at: answers.answered_at,
    },
    delivered_by:
      db?.kind === 'OTHER'
        ? { name: db.name.trim(), phone: db.phone, photo_media_id: db.photo_media_id ?? '' }
        : 'SELF',
    bags_brought: d.bags_brought ?? 0,
    probe_draws_done: d.probe_draws_done,
    lots,
    bags: bagsPayload(d),
    voided_tags: d.voided_tags,
    refused_scans: d.refused_scans,
    moisture: moisturePayload(d),
    sample: d.sample
      ? {
          id: d.sample.id,
          seal_payload: d.sample.seal_payload,
          seal_entry: d.sample.seal_entry,
          weight_g: d.sample.weight_g ?? undefined,
          collected_at: d.sample.collected_at,
          photo_media_id: d.sample.photo_media_id ?? undefined,
          underweight_allowed_by: d.sample.underweight_allowed_by,
        }
      : null,
    outcome: { chosen: 'ACCEPT', accepted_at: d.outcome.accepted_at },
    disputes: disputesPayload(d),
    slip: {
      slip_no: d.slip.slip_no,
      photo_media_id: d.slip.photo_media_id,
      farmer_agreed_at: d.slip.farmer_agreed_at,
      account_confirmed: d.slip.account_confirmed,
      disputed: d.slip.disputed,
    },
    receipt_slip: d.paper?.book_id
      ? {
          id: d.paper.receipt_slip_id,
          book_id: d.paper.book_id,
          slip_no: d.paper.slip_no,
          photo_id: d.paper.page_photo_media_id ?? d.slip.photo_media_id,
        }
      : null,
    closed_at: ctx.closed_at,
  }
}

/**
 * V1 commit (Seva Mandir): price and amount from the card with no Accept tap, no typed bag count,
 * the field picked on the crop screen (else the server takes his registered one), no sample
 * weight or photo, no season light.
 */
function buildCommitPayloadV1(
  d: DeliveryDraft,
  ctx: { cards: Partial<Record<'MAIN' | 'CONV', CardRef>>; closed_at: string },
): CommitDeliveryPayload {
  if (!d.slip.slip_no || !d.slip.photo_media_id) throw new Error('slip missing')
  // Every bag ended up red-tie: no good-bag lot, so a seal sealed early is voided, not bound.
  const sealNotNeeded = d.sample !== null && !hasMainLot(d)
  const answers = d.storage
  if (!answers.stored_over_7_days || !answers.tablet_or_powder || !answers.answered_at) {
    throw new Error('storage answers missing')
  }
  if (d.bags.length === 0) throw new Error('no bags')
  const lots: LotPayload[] = lotsWithFigures(d, ctx.cards).map(({ plan, card, figures }) => {
    if (!card) throw new Error(`no rate card for ${plan.stream}`)
    return {
      id: plan.id,
      code: plan.code,
      stream: plan.stream,
      crop_id: d.crop_id!,
      // The field she picked on the crop screen; none (older drafts) → the server takes his
      // registered one for the crop.
      crop_variety_id: d.cultivation_id ? (d.crop_variety_id ?? null) : null,
      cultivation_id: d.cultivation_id ?? null,
      no_cultivation_reason: null,
      rate_card_id: card.id,
      phone_figures: figures,
    }
  })
  return {
    ...(d.reading_photos_required ? { reading_photos_required: true as const } : {}),
    ...purchaseGpsPayload(d),
    delivery_ulid: d.delivery_ulid,
    session_id: d.session_id,
    farmer_id: d.farmer.id,
    crop_id: d.crop_id!,
    identified_at: d.identified_at,
    identification: {
      method: d.identification.method,
      ...(d.identification.phone_match ? { phone_match: d.identification.phone_match } : {}),
      ...(d.identification.new_photo_media_id
        ? { new_photo_media_id: d.identification.new_photo_media_id }
        : {}),
      photo_confirmed: true,
      candidates_shown: d.identification.candidates_shown,
    },
    storage_answers: {
      stored_over_7_days: answers.stored_over_7_days,
      tablet_or_powder: answers.tablet_or_powder,
      answered_at: answers.answered_at,
    },
    delivered_by: 'SELF',
    lots,
    bags: bagsPayload(d),
    voided_tags: sealNotNeeded
      ? [
          ...d.voided_tags,
          {
            qr_payload: d.sample!.seal_payload,
            kind: 'SEAL' as const,
            void_reason: 'OTHER' as const,
            container_id: null,
            occurred_at: ctx.closed_at,
          },
        ]
      : d.voided_tags,
    refused_scans: d.refused_scans,
    moisture: moisturePayload(d),
    sample:
      d.sample && !sealNotNeeded
        ? {
            id: d.sample.id,
            seal_payload: d.sample.seal_payload,
            seal_entry: d.sample.seal_entry,
            collected_at: d.sample.collected_at,
            underweight_allowed_by: null,
          }
        : null,
    outcome: { chosen: 'ACCEPT', accepted_at: ctx.closed_at },
    disputes: [],
    slip: {
      slip_no: d.slip.slip_no,
      photo_media_id: d.slip.photo_media_id,
      farmer_agreed_at: d.slip.farmer_agreed_at,
      account_confirmed: d.slip.account_confirmed,
      disputed: false,
    },
    receipt_slip: null,
    closed_at: ctx.closed_at,
  }
}

export function buildVoidPayload(d: DeliveryDraft, voided_at: string): VoidDeliveryPayload {
  if (!d.cancel) throw new Error('no cancel in progress')
  // Every lot code minted for this draft is written VOID so it is never reused.
  const a = lotPlan(d)[0]
  const lots: VoidDeliveryPayload['lots'] = [
    { id: d.main_lot.id, code: d.main_lot.code, stream: a?.stream ?? 'MAIN' },
  ]
  if (d.conv_lot) lots.push({ id: d.conv_lot.id, code: d.conv_lot.code, stream: 'CONV' })
  const paper = d.cancel.reason === 'MOVED_TO_PAPER'
  // V1: no scans — the server voids every bag's tag from bags[], and the seal from voided_seal.
  const sealVoid = d.sample
    ? (d.voided_tags.find((v) => v.qr_payload === d.sample!.seal_payload) ??
      (isV1(d) ? { qr_payload: d.sample.seal_payload, occurred_at: voided_at } : undefined))
    : undefined
  return {
    delivery_ulid: d.delivery_ulid,
    session_id: d.session_id,
    farmer_id: d.farmer.id,
    crop_id: d.crop_id,
    crop_variety_id: d.crop_variety_id,
    cultivation_id: d.another_field ? null : d.cultivation_id,
    no_cultivation_reason: d.another_field ? 'FIELD_NOT_ENROLLED' : null,
    lots,
    reason: d.cancel.reason,
    reason_code: d.cancel.reason_code,
    bags: paper ? [] : bagsPayload(d),
    voided_tags: paper
      ? []
      : d.voided_tags.filter((v) => v.kind === 'BAG' || v.qr_payload !== d.sample?.seal_payload),
    voided_seal:
      !paper && sealVoid
        ? { qr_payload: sealVoid.qr_payload, occurred_at: sealVoid.occurred_at }
        : null,
    refused_scans: d.refused_scans,
    moisture: d.moisture.readings.length > 0 ? moisturePayload(d) : null,
    disputes: disputesPayload(d),
    voided_at,
  }
}
